import useAxiosPrivateToken from "../hooks/usePrivateAxios";
import ResponseStatus from "../types/responseStatus.type";
import {
    CancelPermitOtpApiResType, UpdateVehicleOtpApiApiResType
} from "../interface/API/homePageApi.interface";
import {
    isCancelPermitOtpApiResValid,
    isRenewedPermitOtpApiResValid,
    isUpdateVehicleOtpApiResValid
} from "../validations/api/homePageApiValidations";
import { IFileDataBase64 } from "../interface/fileDataBase64.interface";
import { Languages } from "../enums/languages";
import { ApiResponseError } from "../errors/api-response-error";


const CANCEL_PERMIT_OTP_API = '/homePage/cancel-permit';
const ADD_FILE_TO_REQUEST_API = '/homePage/add-file-to-request';
const UPDATE_VEHICLE_DETAILS_API = '/homePage/update-vehicle-details-otp';
const RENEWED_PERMIT_OTP_API = '/homePage/renewed-permit-request-otp';

interface IDisplayRenewedPermitConfirmation {
    requestId: number;
    paymentProcessResult: {
        asmachta: string;
        hasReceipt: boolean;
        paymentAmount: number;
        paymentMethod: string;
        receiptUrl: string;
    }
}

type RenewedPermitFormOtpResApi = {
    isSuccess: boolean;
    message: string;
    displayRenewedPermitConfirmation: IDisplayRenewedPermitConfirmation;
}

type RenewedPermitOtpApiPayload = {
    personId: string;
    vehicleNum: string;
    otpCode: string;
    citizenComment: string;
    isCompany: boolean;
    scanPersonId: IFileDataBase64 | null;
    scanCarId: IFileDataBase64 | null;
    scanWorkStatment: IFileDataBase64 | null;
    card: {
        cardAddedNumber: string;
        cardExpireDateMM: string;
        cardExpireDateYY: string;
        cardHolderId: string;
        cardHolderName: string;
        cardNumber: string;
    }
}

interface ICheckOtpFormFApi {
    personId: string;
    vehicleNum: string;
    otpCode: string;
    isPermit: boolean;
}

interface IUpdateVehicleOtpFormFApi {
    personId: string;
    vehicleNum: string;
    otpCode: string;
    isPermit: boolean;
    newVehicleNum: string;
    newColorId: number;
    newTypeId: number;
    newVehicleGroupId: number;
    citizenComment: string;
    isCompany: boolean;
    scanCarId: IFileDataBase64;
    scanWorkStatment: IFileDataBase64;
    carIdFileName: string;
    workStatementFileName: string;
}

interface IAddFileToRequestPayload {
    personId: string;
    vehicleNum: string;
    otpCode: string;
    file: IFileDataBase64;
}

export default function useHomePageApi() {


    const { axiosLolatechPortalPrivate, handleError } = useAxiosPrivateToken();


    async function updateVehicleOtpApi(form: IUpdateVehicleOtpFormFApi): Promise<UpdateVehicleOtpApiApiResType> {

        const result: UpdateVehicleOtpApiApiResType = {
            isSuccess: true,
            message: '',
            requestId: -1
        }

        try {

            const response = await axiosLolatechPortalPrivate.post(UPDATE_VEHICLE_DETAILS_API, form);
            const data = response.data;

            if (!isUpdateVehicleOtpApiResValid(data.data)) {

                throw new ApiResponseError('Error validating update vehicle details OTP API response');
            }

            result.requestId = data.data.requestId;

        } catch (error) {

            result.isSuccess = false;
            result.message = handleError(error);
        }

        return result;
    }
    
    async function cancelPermitOtpApi(form: ICheckOtpFormFApi): Promise<CancelPermitOtpApiResType> {

        const result: CancelPermitOtpApiResType = {
            isSuccess: true,
            permitIsCanceled: false,
            errorName: '',
            message: ''
        }

        try {

            const response = await axiosLolatechPortalPrivate.post(CANCEL_PERMIT_OTP_API, form);
            const data = response.data;

            if (!isCancelPermitOtpApiResValid(data) || data.status !== ResponseStatus.SUCCESS || !data.data.permitIsCanceled) {

                throw new ApiResponseError('Error validating cancel permit OTP API response or permit was not canceled');
            }

            result.permitIsCanceled = data.data.permitIsCanceled;

        } catch (error) {

            result.isSuccess = false;
            result.message = handleError(error);
        }

        return result;
    }

    async function addFileToRequestOtpApi(formData: IAddFileToRequestPayload) {

        const result = {
            isSuccess: false,
            message: '',
        }

        try {

            const reqToSend = JSON.parse(JSON.stringify(formData));

            const response = await axiosLolatechPortalPrivate.post(ADD_FILE_TO_REQUEST_API, reqToSend);

            const data = response.data;

            if (!data?.status || data.status !== ResponseStatus.SUCCESS) {

                throw new ApiResponseError('Error validating add file to request OTP API response or file was not added to request');
            }

            result.isSuccess = true;
        } catch (error) {

            const objMsg = {
                PORTAL_ACTION_NOT_ALLOWED: {
                    [Languages.Hebrew]: { default: 'לא ניתן להוסיף קובץ לבקשה שאושרה' },
                    [Languages.Arabic]: { default: 'لا يمكن إضافة ملف لطلب تم الموافقة عليه' },
                    [Languages.English]: { default: 'Cannot add file to an approved request' },
                }
            }
            
            result.isSuccess = false;
            result.message = handleError(error, objMsg);
        }

        return result;
    }

    async function renewedPermitOtpApi(form: RenewedPermitOtpApiPayload): Promise<RenewedPermitFormOtpResApi> {

        const result: RenewedPermitFormOtpResApi = {
            isSuccess: true,
            message: '',
            displayRenewedPermitConfirmation: {
                requestId: 0,
                paymentProcessResult: {
                    asmachta: '',
                    hasReceipt: false,
                    paymentAmount: 0,
                    paymentMethod: '',
                    receiptUrl: '',
                }
            },
        }

        try {

            const response = await axiosLolatechPortalPrivate.post(RENEWED_PERMIT_OTP_API, form);
            const data = response.data;

            if (!isRenewedPermitOtpApiResValid(data)) {

                throw new ApiResponseError('Error validating renewed permit OTP API response');
            }

            result.displayRenewedPermitConfirmation = data;

        } catch (error) {

            result.isSuccess = false;
            result.message = handleError(error);
        }

        return result;
    }

    return {
        cancelPermitOtpApi, addFileToRequestOtpApi,
        updateVehicleOtpApi, renewedPermitOtpApi
    };
}